"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import ScrollAnimatedSnakePath from "@/components/common/animations/ScrollAnimatedSnakePath";
import { MobileServiceStepCard, ServiceStepData } from "./ServiceStepCard";

interface MobileStepWithPathProps {
  step: ServiceStepData;
  index: number;
  /** Custom path definitions for each step */
  pathDefinitions?: string[];
}

const DEFAULT_PATHS = [
  "M 50 20 L 50 30 L 15 30 L 15 100", // First: Down, left turn, down
  "M 15 5 L 15 30 L 85 30 L 85 100", // Second: Down, right turn, down
  "M 85 5 L 85 30 L 15 30 L 15 100", // Third: Down, left turn, down
];

/**
 * Mobile step component with animated snake path that triggers content fade-in
 */
export default function MobileStepWithPath({
  step,
  index,
  pathDefinitions = DEFAULT_PATHS,
}: MobileStepWithPathProps) {
  const pathRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(pathRef, { once: true, amount: 0.3 });
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isInView) {
      // Show content after path animation completes (1.5s)
      const timer = setTimeout(() => setShowContent(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isInView]);

  const pathD = pathDefinitions[index] || DEFAULT_PATHS[0];

  return (
    <div ref={pathRef} className="relative">
      {/* Snake Path SVG */}
      <div className="relative h-[200px] w-full overflow-visible">
        <ScrollAnimatedSnakePath
          pathD={pathD}
          useViewAnimation={true}
          animationDuration={1.5}
          strokeWidth={0.5}
          className="absolute top-0 left-0 h-full w-full overflow-visible"
        />
      </div>

      {/* Content - Shows after path animation */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="pb-16"
      >
        <MobileServiceStepCard step={step} index={index} />
      </motion.div>
    </div>
  );
}

interface MobileSequentialStepsProps {
  steps: ServiceStepData[];
  pathDefinitions?: string[];
}

/**
 * Container for mobile sequential steps with path animations
 */
export function MobileSequentialSteps({
  steps,
  pathDefinitions,
}: MobileSequentialStepsProps) {
  return (
    <div className="space-y-0">
      {steps.map((step, index) => (
        <MobileStepWithPath
          key={index}
          step={step}
          index={index}
          pathDefinitions={pathDefinitions}
        />
      ))}
    </div>
  );
}
